<template>
  <div class="image-gallery">
    <div
      v-if="showUploadButton"
      class="gallery-upload-row"
      :class="'gallery-upload-row--' + uploadButtonAlign"
    >
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        multiple
        class="gallery-upload-input"
        @change="onFileChange"
      />
      <v-btn
        small
        outlined
        class="gallery-upload-btn"
        @click="$refs.fileInput && $refs.fileInput.click()"
      >
        <v-icon left small>mdi-upload</v-icon>
        Upload from PC
      </v-btn>
      <span class="gallery-upload-row-actions"><slot name="upload-row-actions" /></span>
    </div>
    <div :class="{ 'gallery-grid-scroll': scrollGridOnly }">
    <v-row class="gallery-grid" no-gutters>
      <v-col v-for="item in galleryItems" :key="item.id" cols="3" class="pa-1">
        <div
          class="gallery-item rounded-lg"
          :class="{
            'gallery-item--selected': selectable && isSelected(item),
            'gallery-item--not-selectable': !selectable,
          }"
        >
          <div
            class="gallery-item__thumb"
            role="button"
            :tabindex="selectable ? 0 : -1"
            @click="selectable ? selectItem(item) : onView(item)"
            @mouseenter="hoveredId = item.id"
            @mouseleave="hoveredId = null"
            @keydown.enter.prevent="selectable ? selectItem(item) : onView(item)"
            @keydown.space.prevent="selectable ? selectItem(item) : onView(item)"
          >
            <img
              v-if="getImageUrl(item)"
              :src="getImageUrl(item)"
              :alt="item.label || item.id"
              class="gallery-item__img"
              draggable="false"
            />
            <div v-else class="placeholder-art">
              <div class="placeholder-art__sky" />
              <div class="placeholder-art__cloud" />
              <div class="placeholder-art__hills" />
            </div>
            <div v-show="hoveredId === item.id" class="gallery-item__overlay">
              <v-btn small outlined class="view-btn" @click.stop="onView(item)"> View </v-btn>
            </div>
            <div v-if="selectable && isSelected(item)" class="gallery-item__check">
              <v-icon small class="gallery-item__check-icon">mdi-checkbox-marked</v-icon>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
    </div>

    <FullscreenImageViewer
      v-model="viewerOpen"
      :image-url="viewerImageUrl"
      :deletable-nano-banana-filename="viewerNanoBananaFilename"
      @nano-banana-deleted="$emit('nano-banana-deleted', $event)"
    />
  </div>
</template>

<script>
import FullscreenImageViewer from './FullscreenImageViewer.vue'

export default {
  name: 'ImageGallery',
  components: { FullscreenImageViewer },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [String, Number, Array],
      default: null,
    },
    allowMultiple: {
      type: Boolean,
      default: false,
    },
    imageKey: {
      type: String,
      default: 'imageUrl',
    },
    showUploadButton: {
      type: Boolean,
      default: true,
    },
    uploadButtonAlign: {
      type: String,
      default: 'right',
      validator: (v) => ['left', 'right', 'center'].includes(v),
    },
    selectable: {
      type: Boolean,
      default: true,
    },
    /** When true, only the grid scrolls (upload row stays fixed); avoids clipping badge/overflow. */
    scrollGridOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hoveredId: null,
      viewerOpen: false,
      viewerImageUrl: null,
      viewerNanoBananaFilename: null,
      selectedIds: [],
      uploadedItems: [],
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (this.allowMultiple) {
          this.selectedIds = Array.isArray(v) ? [...v] : (v != null ? [v] : [])
        } else {
          this.selectedIds = v != null && v !== '' ? [v] : []
        }
      },
    },
  },
  computed: {
    normalizedPropItems() {
      const raw = this.items || []
      const seenIds = new Set()
      const normalized = raw.map((item, index) => {
        const obj = typeof item === 'object' && item !== null ? item : {}
        const id = obj.id !== undefined && obj.id !== null ? obj.id : `gallery-item-${index}`
        return { ...obj, id }
      })
      return normalized.filter((item) => {
        if (seenIds.has(item.id)) return false
        seenIds.add(item.id)
        return true
      })
    },
    galleryItems() {
      const combined = [...this.uploadedItems, ...this.normalizedPropItems]
      const byId = new Map()
      for (const item of combined) {
        const id = item.id != null ? item.id : item[this.imageKey]
        if (id != null && !byId.has(id)) byId.set(id, item)
      }
      return Array.from(byId.values())
    },
    isSelected() {
      return (item) => this.selectedIds.includes(item.id)
    },
  },
  methods: {
    getImageUrl(item) {
      const url = item[this.imageKey]
      return url && typeof url === 'string' ? url : null
    },
    selectItem(item) {
      if (this.allowMultiple) {
        const idx = this.selectedIds.indexOf(item.id)
        if (idx === -1) {
          this.selectedIds.push(item.id)
        } else {
          this.selectedIds.splice(idx, 1)
        }
      } else {
        this.selectedIds = this.selectedIds[0] === item.id ? [] : [item.id]
      }
      this.emitValue()
      this.$emit('select', item)
    },
    emitValue() {
      if (this.allowMultiple) {
        this.$emit('input', [...this.selectedIds])
      } else {
        this.$emit('input', this.selectedIds[0] || null)
      }
    },
    onView(item) {
      this.viewerImageUrl = this.getImageUrl(item)
      this.viewerNanoBananaFilename =
        item && item.nanoBananaFilename && String(item.nanoBananaFilename).trim()
          ? String(item.nanoBananaFilename).trim()
          : null
      this.viewerOpen = true
      this.$emit('view', item)
    },
    onFileChange(e) {
      const input = e.target
      const files = input && input.files ? Array.from(input.files) : []
      input.value = ''
      if (!files.length) return
      const base = `upload-${Date.now()}`
      files.forEach((file, i) => {
        if (!file.type.startsWith('image/')) return
        const url = URL.createObjectURL(file)
        this.uploadedItems.push({
          id: `${base}-${i}`,
          imageUrl: url,
          label: file.name || `Upload ${this.uploadedItems.length + 1}`,
        })
      })
      this.$emit('uploaded', this.uploadedItems.slice())
    },
  },
}
</script>

<style scoped>
.image-gallery {
  width: 100%;
}

.gallery-upload-row {
  margin-bottom: 12px;
  display: flex;
}
.gallery-upload-row--left {
  justify-content: flex-start;
}
.gallery-upload-row--right {
  justify-content: flex-end;
}
.gallery-upload-row--center {
  justify-content: center;
}
.gallery-item--not-selectable .gallery-item__thumb {
  cursor: pointer;
}

.gallery-upload-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
}

.gallery-upload-btn {
  flex-shrink: 0;
}

.gallery-upload-row-actions {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
}

.gallery-grid-scroll {
  min-height: 0;
  overflow-y: auto;
  max-height: 28rem;
}

.gallery-grid-scroll .gallery-grid {
  max-height: none;
  overflow: visible;
}

.gallery-grid {
  max-height: 28rem;
  overflow-y: auto;
}

.gallery-item {
  background: #2d2d2d !important;
  border: 1px solid #303030 !important;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
}

.gallery-item:hover {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.gallery-item--selected {
  border-color: rgba(26, 115, 232, 0.6) !important;
  box-shadow: 0 0 0 1px rgba(26, 115, 232, 0.3);
}

.gallery-item__thumb {
  position: relative;
  padding-bottom: 100%;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
}

.gallery-item__img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder-art {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.placeholder-art__sky {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 55%;
  background: linear-gradient(180deg, #87ceeb 0%, #b0e0e6 100%);
}

.placeholder-art__cloud {
  position: absolute;
  top: 12%;
  left: 50%;
  transform: translateX(-50%);
  width: 40%;
  height: 14%;
  background: #fff;
  border-radius: 50%;
  opacity: 0.95;
}

.placeholder-art__hills {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(180deg, #7cb342 0%, #558b2f 100%);
  border-radius: 50% 50% 0 0 / 30% 30% 0 0;
  box-shadow: 0 -8px 0 rgba(0, 0, 0, 0.06);
}

.gallery-item__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-btn {
  border-color: #fff;
  color: #fff;
}

.gallery-item__check {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.gallery-item__check-icon {
  color: #fff !important;
}
</style>
